import React from "react";
import { useSelector } from "react-redux";
import { configContext } from "../../../components/config-provider";
import { listLocks, LockRecord, releaseLock } from "../../../oauth";
import { IApplicationState } from "../../../state/ducks";
import { AuthReducerState } from "../../../state/ducks/auth";

/**
 * Locked accounts, and the admin action to release them.
 *
 * Talks to the auth server directly with the administrator's own token, so
 * the auth server's audit log records which human performed the unlock. That
 * requires the admin to hold `/locks` there — see the deployment guide. Page
 * *visibility* is a separate concern, governed by appAccess.
 */
const formatWhen = (value?: string) => {
  if (!value) return "—";
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? "—" : at.toLocaleString();
};

const scopeLabel = (lock: LockRecord) => {
  switch (lock.scope) {
    case "account":
      return "Account";
    case "ip":
      return "IP address";
    default:
      return "Account + IP";
  }
};

export const AccountManagement: React.FC = () => {
  const { authServer } = React.useContext(configContext);
  const auth = useSelector<IApplicationState, AuthReducerState>((state) => state.auth);
  const token = auth.token.accessToken;

  const [locks, setLocks] = React.useState<LockRecord[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [releasing, setReleasing] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    if (!authServer || !token) return;
    setLoading(true);
    setError(null);
    const { locks: result, error: err } = await listLocks(authServer, token);
    setLoading(false);
    if (err) {
      setError(err);
      return;
    }
    setLocks(result ?? []);
  }, [authServer, token]);

  React.useEffect(() => {
    load();
  }, [load]);

  const unlock = async (lock: LockRecord) => {
    setReleasing(lock._id);
    setError(null);
    setNotice(null);
    const { error: err } = await releaseLock(authServer, token, lock._id);
    setReleasing(null);
    if (err) {
      setError(err);
      return;
    }
    setNotice(`Unlocked ${lock.username || lock.target}.`);
    // Re-read rather than splicing locally: the release also resets the
    // failure counter, and another admin may have acted in the meantime.
    load();
  };

  return (
    <div className="account-management">
      <div className="am-header">
        <div>
          <div className="am-title">ACCOUNT MANAGEMENT</div>
          <div className="am-subtitle">
            Accounts locked after too many failed sign-in attempts. Unlocking clears the
            failure count and lets the user sign in again.
          </div>
        </div>
        <button className="am-refresh" onClick={load} disabled={loading}>
          {loading ? "LOADING…" : "REFRESH"}
        </button>
      </div>

      {error && <div className="am-message error">{error}</div>}
      {notice && <div className="am-message success">{notice}</div>}

      <table>
        <thead>
          <tr>
            <th>Username</th>
            <th>Scope</th>
            <th>Failed attempts</th>
            <th>Times locked</th>
            <th>IP address</th>
            <th>Locked since</th>
            <th>Unlocks at</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {locks.map((lock) => (
            <tr key={lock._id}>
              <td>{lock.username || "—"}</td>
              <td>
                <span className="am-scope">{scopeLabel(lock)}</span>
              </td>
              <td>{lock.failedCount}</td>
              <td>{lock.lockCount}</td>
              <td>{lock.connectingIp || "—"}</td>
              <td>{formatWhen(lock.lockedAt || lock.updatedAt)}</td>
              {/* No expiredAt means the policy is untilAdminUnlock: it stays
                  locked until someone here releases it. */}
              <td>{lock.expiredAt ? formatWhen(lock.expiredAt) : "Admin unlock only"}</td>
              <td>
                <button
                  className="am-unlock"
                  onClick={() => unlock(lock)}
                  disabled={releasing === lock._id}
                >
                  {releasing === lock._id ? "UNLOCKING…" : "UNLOCK"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {locks.length === 0 && !loading && !error && (
        <div className="am-empty">No locked accounts.</div>
      )}
    </div>
  );
};
